import React from "react";
import { ChakraProvider, theme } from "@chakra-ui/react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ApolloProvider } from "@apollo/client";
import { apolloClient } from "./apollo/client";
import Home from "./pages/Home";

// Client-related imports
import NewClientForm from "./pages/clients/NewClientForm"; 
import ClientsList from "./pages/clients/ClientsList";
import ClientDetails from "./pages/clients/ClientDetails";

// Website-related imports
import NewWebsiteForm from "./pages/tenantwebsites/NewWebsiteForm"; 
import WebsitesList from "./pages/tenantwebsites/WebsitesList";
import WebsiteDetails from "./pages/tenantwebsites/WebsiteDetails";

// Bill-related imports
import Bills from "./pages/bills/Bills";
import BillDetails from "./pages/bills/BillDetails";
import NewBill from "./pages/bills/NewBill";

// Product-related imports
import NewProductForm from "./pages/products/NewProductForm";
import ProductView from "./pages/products/ProductView";
import AllProducts from "./pages/products/AllProducts";
import Cart from "./pages/products/Cart";  
import OrderDetails from "./pages/products/OrderDetails";

// Project-related imports
import NewProject from "./pages/projects/new";
import ProjectPage from "./pages/projects/ProjectPage";
import ProjectsList from "./pages/projects/ProjectsList";
import TimelineView from "./pages/projects/TimelineView";

// Session/Booking-related imports
import { ClientBookingCalendar } from "./pages/sessions/ClientBookingCalendar";
import { ScheduleSession } from "./pages/sessions/ScheduleSession";
import { EditSessionType } from "./pages/sessions/EditSessionType";
import { CreateSessionType } from "./pages/sessions/CreateSessionType";
import { SessionTypesPage } from "./pages/sessions/SessionTypes";
import { SetAvailabilities } from "./pages/sessions/SetAvailabilities";
import { BookingDetails } from "./pages/sessions/BookingDetails";
import { ClientBookingCalendarByPractitionerId } from "./pages/sessions/ClientBookingCalendarByPractitionerId";

// Admin and dashboard imports
import AdminDashboard from "./pages/AdminDashboard";

// SMS Podcast Funnel imports
import Property from "./pages/Property";
import P1 from "./pages/P1";
import InvestmentResults from "./pages/InvestmentResults";

// Test and development imports
import SendTestEmail from "./pages/test/SendTestEmail";
import TestUploadFile from "./pages/test/TestUploadFile";
import TestUploadEncryptedFile from "./pages/test/TestUploadEncryptedFile";
import TestUploadUnencryptedFile from "./pages/test/TestUploadUnencryptedFile";
import TestAuth from "./pages/test/TestAuth";
import TestClientCart from "./pages/test/TestClientCart";
import TestUploadFileToPinata from "./pages/test/TestUploadFileToPinata";

// Static pages and misc
import CustomWebsiteDevelopment from "./pages/CustomWebsiteDevelopment";
import ViewFile from "./pages/ViewFile";
import Blog from "./pages/Blog";
import News from "./pages/News";
import HundredMembers from "./pages/100Members";
import EcosystemAgreement from "./pages/EcosystemAgreement";
import AboutUs from "./pages/AboutUs";
import Welcome from "./pages/Welcome";
import Agreement from "./pages/Agreement";
import Founders from "./pages/Founders";
import GoverningRules from "./pages/GoverningRules";
import SignedAgreement from "./pages/SignedAgreement";
import Governance from "./pages/Governance";
import IntroSession from "./pages/IntroSession";
import Services from "./pages/Services";

import Event1Page from "./pages/events/tom/1";
import Event2Page from "./pages/events/tom/2";
import Event3Page from "./pages/events/tom/3";
import Event4Page from "./pages/events/tom/4";
import Block52EventPage from "./pages/events/tom/Block52";
import ChrisEventPage from "./pages/events/tom/chris";

// Contexts
import { AuthProvider } from "./contexts/AuthContext";

const AppContent = () => {
  return (
    <Routes>
      {/* Home page */}
      <Route path="/" element={<Home />} />
      
      {/* Event routes */}
      <Route path="/events/tom/1" element={<Event1Page />} />
      <Route path="/events/tom/2" element={<Event2Page />} />
      <Route path="/events/tom/3" element={<Event3Page />} />
      <Route path="/events/tom/4" element={<Event4Page />} />
      <Route path="/events/tom/block52" element={<Block52EventPage />} />
      <Route path="/events/tom/chris" element={<ChrisEventPage />} />
      {/* Client routes */}
      <Route path="/newclient" element={<NewClientForm />} />
      <Route path="/clients" element={<ClientsList />} />
      <Route path="/client/:id" element={<ClientDetails />} />
      
      {/* Website routes */}
      <Route path="/websites/new" element={<NewWebsiteForm />} />
      <Route path="/websites" element={<WebsitesList />} />
      <Route path="/website/:id" element={<WebsiteDetails />} />
      
      {/* Bills routes */}
      <Route path="/bills" element={<Bills />} />
      <Route path="/bills/new" element={<NewBill />} />
      <Route path="/bill/:id" element={<BillDetails />} />
      
      {/* Product routes */}
      <Route path="/products/new" element={<NewProductForm />} />
      <Route path="/products/:id" element={<ProductView />} />
      <Route path="/products" element={<AllProducts />} />
      <Route path="/cart" element={<Cart />} />
      <Route path="/order/:id" element={<OrderDetails />} />
      
      {/* Project routes */}
      <Route path="/projects/new" element={<NewProject />} />
      <Route path="/project/:id" element={<ProjectPage />} />
      <Route path="/project/:id/timeline" element={<TimelineView />} />
      <Route path="/projects" element={<ProjectsList />} />
      
      {/* Session/Booking routes */}
      <Route path="/sessions/types" element={<SessionTypesPage />} />
      <Route path="/sessions/types/create" element={<CreateSessionType />} />
      <Route path="/sessions/edit/:id" element={<EditSessionType />} />
      <Route path="/sessions/set-availabilities" element={<SetAvailabilities />} />
      <Route path="/sessions/set-availabilities/:practitionerId" element={<SetAvailabilities />} />
      <Route path="/sessions/practitioner-calendar/:practitionerId" element={<ClientBookingCalendarByPractitionerId />} />
      <Route path="/schedule-session" element={<ScheduleSession />} />
      <Route path="/sessions/client-booking-calendar" element={<ClientBookingCalendar />} />
      <Route path="/bookings/:bookingId" element={<BookingDetails />} />
      
      {/* SMS Podcast Funnel routes */}
      <Route path="/property" element={<Property />} />
      <Route path="/p1" element={<P1 />} />
      <Route path="/investment-results" element={<InvestmentResults />} />
      
      {/* Admin routes */}
      <Route path="/admin" element={<AdminDashboard />} />
      
      {/* Test routes */}
      <Route path="/test/sendemail" element={<SendTestEmail />} />
      <Route path="/test/upload" element={<TestUploadFile />} />
      <Route path="/test/upload-encrypted" element={<TestUploadEncryptedFile />} />
      <Route path="/test/upload-unencrypted" element={<TestUploadUnencryptedFile />} />
      <Route path="/test/auth" element={<TestAuth />} />
      <Route path="/test/cart" element={<TestClientCart />} />
      <Route path="/test/upload-pinata" element={<TestUploadFileToPinata />} />
      
      {/* Static and misc routes */}
      <Route path="/custom-website-development" element={<CustomWebsiteDevelopment />} />
      <Route path="/file/:hash" element={<ViewFile />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/news" element={<News />} />
      <Route path="/100members" element={<HundredMembers />} />
      <Route path="/ecosystem-agreement" element={<EcosystemAgreement />} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/welcome" element={<Welcome />} />
      <Route path="/agreement" element={<Agreement />} />
      <Route path="/founders" element={<Founders />} />
      <Route path="/governance" element={<Governance />} />
      <Route path="/governing-rules" element={<GoverningRules />} />
      <Route path="/membership-certificate/:id" element={<SignedAgreement />} />
      <Route path="/intro-session" element={<IntroSession />} />
      <Route path="/services" element={<Services />} />
    </Routes> 
  );
};

export const App = () => (
  <Router>
    <ApolloProvider client={apolloClient}>
      <AuthProvider>
        <ChakraProvider theme={theme}>
          <AppContent />
        </ChakraProvider>
      </AuthProvider>
    </ApolloProvider>
  </Router>
);
